import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, ChevronDown } from 'lucide-react';
import { useAgentActivity } from '@/hooks/use-agent-activity';
import { useCssVars } from '@/hooks/use-css-vars';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Agent label map (duplicated to keep component self-contained)
// ---------------------------------------------------------------------------

const AGENT_LABELS: Record<string, string> = {
  'code-vuln': 'Code Vulnerability',
  sast: 'SAST Engine',
  'secret-scanner': 'Secret Scanner',
  'crypto-audit': 'Crypto Audit',
  'quantum-readiness': 'Quantum Readiness',
  'malware-detect': 'Malware Detection',
  dependency: 'Dependency Scanner',
  'license-compliance': 'License Compliance',
  'supply-chain': 'Supply Chain',
  'container-security': 'Container Security',
  'iac-scanner': 'IaC Scanner',
  'cloud-config': 'Cloud Config',
  'config-audit': 'Config Audit',
  'network-security': 'Network Security',
  'cicd-gate': 'CI/CD Gate',
  'api-security': 'API Security',
  'access-control': 'Access Control',
  'data-privacy': 'Data Privacy',
  compliance: 'Compliance',
  'threat-model': 'Threat Model',
  'pentest-recon': 'Attack Surface',
  'incident-response': 'Incident Response',
  'security-score': 'Security Scorecard',
  education: 'Security Education',
};

function label(type: string): string {
  return AGENT_LABELS[type] ?? type;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AgentActivityIndicator() {
  const { activeRuns, recentRuns, totalActive } = useAgentActivity();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Nothing to show
  if (totalActive === 0 && recentRuns.length === 0) {
    return null;
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium transition-colors',
          totalActive > 0
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800/50'
            : 'bg-gray-100 text-muted-foreground dark:bg-muted dark:text-muted-foreground hover:bg-gray-200 dark:hover:bg-muted',
        )}
      >
        {totalActive > 0 ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>{totalActive} running</span>
          </>
        ) : (
          <>
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            <span>{recentRuns.length} done</span>
          </>
        )}
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-xl border border-gray-200 bg-white shadow-lg dark:border-border dark:bg-card z-50 overflow-hidden">
          {/* Active runs */}
          {activeRuns.length > 0 && (
            <div>
              <div className="px-3 py-2 border-b border-gray-100 dark:border-border">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Running
                </p>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {activeRuns.map((run) => (
                  <ActiveRunRow
                    key={run.runId}
                    label={label(run.agentType)}
                    progress={run.progress}
                    progressMessage={run.progressMessage}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Recent runs */}
          {recentRuns.length > 0 && (
            <div>
              {activeRuns.length > 0 && (
                <div className="border-t border-gray-200 dark:border-border" />
              )}
              <div className="px-3 py-2 border-b border-gray-100 dark:border-border">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Recently Completed
                </p>
              </div>
              <div className="max-h-40 overflow-y-auto">
                {recentRuns.map((run) => (
                  <div
                    key={run.runId}
                    className="px-3 py-2.5 border-b border-gray-50 dark:border-border/50 last:border-b-0 flex items-center gap-2"
                  >
                    {run.status === 'completed' ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                    )}
                    <span className="text-xs text-gray-900 dark:text-foreground truncate">
                      {label(run.agentType)}
                    </span>
                    {run.status === 'completed' && run.orgId && run.portfolioId && (
                      <Link
                        to={`/orgs/${run.orgId}/portfolios/${run.portfolioId}/projects/${run.projectId}/agents/runs/${run.runId}`}
                        onClick={() => setOpen(false)}
                        className="ml-auto text-[10px] font-medium text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                      >
                        View Report
                      </Link>
                    )}
                    {run.status === 'failed' && (
                      <span className="ml-auto text-[10px] text-red-700 dark:text-red-400">Failed</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state (shouldn't normally show) */}
          {activeRuns.length === 0 && recentRuns.length === 0 && (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">
              No agent activity
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ActiveRunRowProps {
  label: string;
  progress: number;
  progressMessage?: string;
}

function ActiveRunRow({ label, progress, progressMessage }: ActiveRunRowProps) {
  // Bar width is dynamic per render. Set it via a CSS custom variable on a
  // ref so we don't need an inline `style` attribute (CSP style-src-attr is
  // strict). The min-2% floor keeps the bar visible at low progress.
  const barRef = useCssVars<HTMLDivElement>({
    '--bar-width': `${Math.max(progress, 2)}%`,
  });
  return (
    <div className="px-3 py-2.5 border-b border-gray-50 dark:border-border/50 last:border-b-0">
      <div className="flex items-center gap-2 mb-1.5">
        <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin shrink-0" />
        <span className="text-xs font-medium text-gray-900 dark:text-foreground truncate">{label}</span>
        <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">{progress}%</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-muted overflow-hidden">
        <div
          ref={barRef}
          className="h-full rounded-full bg-blue-500 transition-all duration-500 w-[var(--bar-width)]"
        />
      </div>
      {progressMessage && (
        <p className="text-[10px] text-muted-foreground mt-1 truncate">{progressMessage}</p>
      )}
    </div>
  );
}
